import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { 
  DollarSign, Calendar, RefreshCw, CheckCircle, Clock, 
  CreditCard, Package, Truck, ShoppingBag, AlertCircle
} from 'lucide-react';

const RedemptionHistory = () => {
  const navigate = useNavigate();
  const [redemptions, setRedemptions] = useState([]);
  const [productOrders, setProductOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRedemptions = async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      
      const response = await authFetch('/api/redemption-history');
      if (response.ok) {
        const data = await response.json();
        setRedemptions(data.redemptions || []);
        setProductOrders(data.product_orders || []);
        if (showToast) {
          toast.success('History updated');
        }
      }
    } catch (error) {
      toast.error('Failed to load redemptions');
    } finally {
      setLoading(false);
      if (showToast) {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchRedemptions();
  }, [navigate]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
      case 'processed':
        return (
          <Badge className="bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))] border font-medium px-3 py-1">
            <CheckCircle className="h-3 w-3 mr-1.5" />
            Paid
          </Badge>
        );
      case 'fulfilled':
      case 'dispatched':
        return (
          <Badge className="bg-[hsl(var(--success-light))] text-[hsl(var(--success))] border-[hsl(var(--success))] border font-medium px-3 py-1">
            <Truck className="h-3 w-3 mr-1.5" />
            Dispatched
          </Badge>
        );
      default:
        return (
          <Badge className="bg-[hsl(var(--warning-light))] text-[hsl(var(--warning))] border-[hsl(var(--warning))] border font-medium px-3 py-1">
            <Clock className="h-3 w-3 mr-1.5" />
            Pending
          </Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalItems = redemptions.length + productOrders.length;

  return (
    <Layout>
      <div className="space-y-8" data-testid="redemption-history-page">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row md:items-start md:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Redemption History
            </h1>
            <p className="text-muted-foreground text-lg">
              Track your reward point redemptions and product orders
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => fetchRedemptions(true)}
              disabled={refreshing || loading}
              className="rounded-xl"
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => navigate('/redeem-points')} className="rounded-xl" data-testid="new-redemption-button">
              New Redemption
            </Button>
          </div>
        </motion.div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-2xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : totalItems === 0 ? (
          <Card className="rounded-2xl border-0 shadow-md">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                No Redemptions Yet
              </h3>
              <p className="text-muted-foreground mb-6">
                You haven't redeemed any points yet.
              </p>
              <Button onClick={() => navigate('/redeem-points')} className="rounded-xl">
                Redeem Points Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="all" data-testid="tab-all">
                All ({totalItems})
              </TabsTrigger>
              <TabsTrigger value="cash" data-testid="tab-cash">
                <CreditCard className="h-4 w-4 mr-1.5" />
                Cash ({redemptions.length})
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="tab-products">
                <ShoppingBag className="h-4 w-4 mr-1.5" />
                Products ({productOrders.length})
              </TabsTrigger>
            </TabsList>

            {/* All Tab */}
            <TabsContent value="all" className="space-y-4">
              {/* Combine and sort by date */}
              {[
                ...redemptions.map(r => ({ ...r, _type: 'cash', _date: r.created_at })),
                ...productOrders.map(o => ({ ...o, _type: 'product', _date: o.created_at }))
              ]
                .sort((a, b) => new Date(b._date || 0) - new Date(a._date || 0))
                .map((item, index) => (
                  <motion.div
                    key={item.redemption_id || item.order_id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    {item._type === 'cash' ? (
                      <CashRedemptionCard item={item} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                    ) : (
                      <ProductOrderCard item={item} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                    )}
                  </motion.div>
                ))
              }
            </TabsContent>

            {/* Cash Tab */}
            <TabsContent value="cash" className="space-y-4">
              {redemptions.length === 0 ? (
                <EmptyState icon={CreditCard} message="No cash redemptions yet" />
              ) : (
                redemptions.map((r, index) => (
                  <motion.div
                    key={r.redemption_id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <CashRedemptionCard item={r} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                  </motion.div>
                ))
              )}
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products" className="space-y-4">
              {productOrders.length === 0 ? (
                <EmptyState icon={ShoppingBag} message="No product orders yet" />
              ) : (
                productOrders.map((o, index) => (
                  <motion.div
                    key={o.order_id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <ProductOrderCard item={o} formatDate={formatDate} getStatusBadge={getStatusBadge} />
                  </motion.div>
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
};

const EmptyState = ({ icon: Icon, message }) => (
  <Card className="rounded-2xl border-0 shadow-md">
    <CardContent className="flex flex-col items-center justify-center py-12">
      <Icon className="h-10 w-10 text-muted-foreground mb-3" />
      <p className="text-muted-foreground">{message}</p>
    </CardContent>
  </Card>
);

const CashRedemptionCard = ({ item, formatDate, getStatusBadge }) => (
  <Card className="rounded-2xl border-0 shadow-md" data-testid="cash-redemption-card">
    <CardHeader>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
              <CreditCard className="text-[hsl(var(--success))]" size={18} />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Cash Redemption
            </CardTitle>
            {getStatusBadge(item.status)}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground ml-12">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(item.created_at)}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {item.points_redeemed} pts
          </div>
          <div className="text-sm text-muted-foreground">
            ${item.cash_value}
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {item.bank_details && (
        <div className="bg-muted/50 p-3 rounded-xl">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <CreditCard size={14} />
            Bank Details
          </h4>
          <div className="space-y-1 text-sm">
            <p><span className="text-muted-foreground">Account:</span> {item.bank_details.account_name}</p>
            <p><span className="text-muted-foreground">BSB:</span> <span className="font-mono">{item.bank_details.bsb}</span></p>
            <p><span className="text-muted-foreground">Number:</span> <span className="font-mono">{item.bank_details.account_number}</span></p>
          </div>
        </div>
      )}
      
      {item.status === 'pending' && (
        <div className="mt-3 p-3 bg-[hsl(var(--warning-light))] rounded-xl text-sm">
          <p className="text-[hsl(var(--warning))] font-medium flex items-center gap-2">
            <Clock size={14} />
            Processing: 1-3 business days
          </p>
        </div>
      )}
      
      {(item.status === 'paid' || item.status === 'processed') && (
        <div className="mt-3 p-3 bg-[hsl(var(--success-light))] rounded-xl text-sm">
          <p className="text-[hsl(var(--success))] font-medium flex items-center gap-2">
            <CheckCircle size={14} />
            Paid{item.paid_at ? `: ${formatDate(item.paid_at)}` : ''}
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

const ProductOrderCard = ({ item, formatDate, getStatusBadge }) => (
  <Card className="rounded-2xl border-0 shadow-md" data-testid="product-order-card">
    <CardHeader>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <ShoppingBag className="text-primary" size={18} />
            </div>
            <CardTitle className="text-xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Product Order
            </CardTitle>
            {getStatusBadge(item.status)}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground ml-12">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(item.created_at)}</span>
            {item.order_id && (
              <>
                <span className="text-border">|</span>
                <span className="font-mono text-xs">ID: {item.order_id.substring(0, 8)}...</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {item.points_redeemed} pts
          </div>
          <div className="text-sm text-muted-foreground">
            ${item.cash_value ?? item.points_redeemed}
          </div>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {/* Order Items */}
      {item.items && item.items.length > 0 && (
        <div className="bg-primary/5 p-4 rounded-xl mb-3">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Package size={14} className="text-primary" />
            Order Items
          </h4>
          <div className="space-y-1.5">
            {item.items.map((itm, idx) => (
              <div key={`${itm.product}-${itm.variation}-${idx}`} className="flex justify-between text-sm">
                <span>{itm.quantity}x {itm.product}</span>
                <span className="text-muted-foreground">{itm.variation}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {item.status === 'pending' && (
        <div className="p-3 bg-[hsl(var(--warning-light))] rounded-xl text-sm">
          <p className="text-[hsl(var(--warning))] font-medium flex items-center gap-2">
            <Clock size={14} />
            Awaiting fulfilment by HOLONS
          </p>
        </div>
      )}

      {(item.status === 'fulfilled' || item.status === 'dispatched') && (
        <div className="p-3 bg-[hsl(var(--success-light))] rounded-xl text-sm">
          <p className="text-[hsl(var(--success))] font-medium flex items-center gap-2">
            <Truck size={14} />
            Order dispatched
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

export default RedemptionHistory;
